'use client'

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { AppStoreBadge, PlayStoreBadge } from '@/shared/not-found/components/store-badges'
import { WELCOME_COPY } from './app-invite.constants'
import { useAppInvite } from './app-invite-provider'
import { detectPlatform, type WebPlatform } from './platform'

/**
 * The welcome variant of the Web → App invite (PB-303), presented as a
 * non-blocking bottom bar rather than a full-screen modal.
 *
 * Why a bar and not the shared modal: the welcome nudge is a low-intent "you'd
 * enjoy the app" prompt shown right after sign-in. Fronting it with an opaque
 * scrim that blocks the whole page the moment the user lands is hostile (V1
 * sprint test #11) — they can't read or use the page behind it. So this mirrors
 * `SmartAppBanner`: a `fixed` bar pinned to the bottom, no overlay, so the page
 * stays scrollable and tappable. The higher-intent *gated* variant keeps the
 * scrimmed modal (`AppInviteModal`) because it's a deliberate interruption.
 *
 * Mobile-web only by construction: `showWelcome` (in the provider) refuses to
 * raise a welcome on desktop, so this bar only ever becomes active there. It
 * still hard-checks `suppressed` to never render inside the native app.
 *
 * Dismiss ("Not now" / ✕) routes through the shared `dismiss()`, which starts
 * the 3-day per-user cooldown — identical suppression to the modal's dismiss.
 */
export function AppInviteWelcomeBar() {
  const { active, suppressed, dismiss } = useAppInvite()

  // OS is client-only; resolve after mount (mirrors AppInviteModal). Default
  // 'desktop', but the bar never renders on desktop (welcome is mobile-web only)
  // so the useful value is always ios/android by the time it's visible.
  const [platform, setPlatform] = useState<WebPlatform>('desktop')
  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  // Web-only surface, welcome-only presentation. The gated variant renders in
  // AppInviteModal; anything else here renders nothing.
  if (suppressed) return null
  if (active?.kind !== 'welcome') return null

  return (
    <div
      data-testid="app-invite-welcome-bar"
      role="region"
      aria-label="Get the HubHive app"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-card px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] animate-in slide-in-from-bottom duration-200"
    >
      <div className="mx-auto flex w-full max-w-md items-center gap-3">
        {/* Static local SVG — nothing for next/image to optimize (vector, and
            the optimizer refuses SVG without images.dangerouslyAllowSVG). */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/logo.svg" alt="HubHive" className="h-9 w-9 shrink-0" />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{WELCOME_COPY.title}</p>
          <p className="truncate text-xs text-muted-foreground">{WELCOME_COPY.body}</p>
        </div>

        {/* CTA — on mobile web the OS store badge is the useful action (a QR is
            never right on the device you hold). Show only the device's own store
            to keep the bar compact on a narrow phone; the badge links to the
            store, so no click handler is needed. */}
        <div className="shrink-0">
          {platform === 'android' ? <PlayStoreBadge /> : <AppStoreBadge />}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Not now"
          className="shrink-0 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
